/**
 * Both Home Page endpoints (list and search) return per-module records that
 * describe the same things under different field names. This flattens every
 * module into ONE shape so every downstream component (RecordCard, detail
 * panes, drawers) stays module-agnostic.
 *
 * Known differences between the two integrated modules:
 *
 *                     Discovery              Brand Audit
 *   run date          runDate                start_time
 *   brand domain      brandDomain            brandDomainName
 *   persona           ["General Persona"]    [{ title, description }]
 *   pillars score     brand_pillars_score    brand_pillars_score OR product_dimension
 *   type              type: "Brand"          (absent — inferred from rbiType/scores)
 *
 * `metrics` and `report` pass through untouched — resolveMetrics() in
 * homePageApi.js reads them, and the pillars/product-dimension score key is
 * normalized there rather than here so the raw entries stay intact.
 */
export default function normalizeRecord(raw) {
  if (!raw) return null;

  const personas = Array.isArray(raw.persona)
    ? raw.persona.map((p) => (typeof p === "string" ? p : p?.title || "")).filter(Boolean)
    : [];

  return {
    transactionId: raw.transactionId,
    // getHomePageDataV2 only: "brandAudit" | "categoryAnalysis". Absent on V1
    // records. Lets the merged Brand Assessment tab pick the card/detail pane.
    recordKind: raw.recordKind ?? null,
    brand: raw.brand,
    brandDomain: raw.brandDomain ?? raw.brandDomainName ?? "",
    // Brand Audit records carry no `type`; a product-dimension score is the
    // real signal that it's a product run (same swap Discovery's own records
    // express via type: "Product").
    type: raw.type ?? (hasProductDimension(raw) ? "Product" : "Brand"),
    category: toArray(raw.category),
    market: toArray(raw.market),
    llm: toArray(raw.llm),
    persona: personas,
    // transactionStatus is the authoritative field when present (Brand
    // Audit/Category Analysis can carry a stale top-level `status` that
    // disagrees with it, e.g. "INITIATED" vs. the real "COMPLETED") —
    // Discovery's records never have transactionStatus at all, so this
    // falls through to `status` there unchanged.
    status: raw.transactionStatus ?? raw.status,
    runDate: raw.runDate ?? raw.start_time ?? null,
    metrics: Array.isArray(raw.metrics) ? raw.metrics : [],
    report: raw.report ?? null,
    // Brand Audit only — the LLM whose combination drives the summary shown
    // above its per-LLM table.
    primaryLLM: raw.primaryLLM ?? null,
    competitor: raw.competitor ?? null,
    // Test vs Final run — Brand Audit and Category Analysis both require a
    // Test run before Final is launchable and carry this field; Discovery
    // records simply don't have it, so the card's Test/Final chip (driven
    // off this field) naturally never renders there.
    auditType: raw.audit_type ?? raw.run_type ?? null,
    // Category Analysis only — which metrics ("rank"/"sentiment"/
    // "coreAttribute"/"categoryDimension"/"signalStrength") this run was
    // launched with. Every other module's records simply don't have it, and
    // CategoryAnalysisDetailPane treats a missing/empty value as "all 4
    // RBI metrics selected" (today's behavior), so this is purely additive.
    selectedMetrics: Array.isArray(raw.selectedMetrics) ? raw.selectedMetrics : null,
    // Brand Audit / Category Analysis only — the record's Test runs
    // ({ transactionId, status }). Used to pick which transaction to open
    // on the real page (see resolveOpenTransactionId in homePageApi.js).
    testIds: Array.isArray(raw.testIds) ? raw.testIds : [],
  };
}

function toArray(value) {
  if (Array.isArray(value)) return value;
  return value ? [value] : [];
}

function hasProductDimension(raw) {
  return (raw.metrics || []).some((entry) => entry?.scores && "product_dimension" in entry.scores);
}
